import { Platform } from "react-native";

import { createMemoryStore } from "./memoryStore";
import type { OfflineStore } from "./types";

let singleton: OfflineStore | null = null;
let sqliteFailed = false;

/**
 * expo-sqlite nur hier. Auf Web ruft getOfflineStore das nicht auf.
 * Der Worker startet nicht beim Laden, sondern in openDatabaseAsync
 * (expo-sqlite 16.0.10, web/SQLiteModule.ts, getWorker()).
 */
export async function openSqliteStore(): Promise<OfflineStore> {
  const SQLite = await import("expo-sqlite");
  const db = await SQLite.openDatabaseAsync("ferina-p8.db");
  await db.execAsync(`
    CREATE TABLE IF NOT EXISTS kv (
      k TEXT PRIMARY KEY NOT NULL,
      v TEXT NOT NULL
    );
  `);
  const memory = createMemoryStore();
  const row = await db.getFirstAsync<{ v: string }>(
    "SELECT v FROM kv WHERE k = ?",
    ["state"]
  );
  if (row?.v) await memory.hydrate(row.v);

  const persist = async () => {
    const raw = await memory.snapshot();
    await db.runAsync(
      "INSERT OR REPLACE INTO kv (k, v) VALUES (?, ?)",
      "state",
      raw
    );
  };

  const mutating = new Set([
    "upsertOutbox",
    "removeOutbox",
    "setBundle",
    "upsertLocalCase",
    "appendLocalEntry",
    "setDueAnchors",
    "upsertMedia",
    "upsertConflict",
    "deleteConflict",
    "addNotice",
    "upsertKeyedNotice",
    "removeNoticesByKeyPrefix",
    "markNoticeRead",
    "evictCases",
    "remapCaseId",
    "purgeSitesNotIn",
    "clearAll",
    "hydrate",
  ]);

  return new Proxy(memory, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== "function") return value;
      return async (...args: unknown[]) => {
        const result = await value.apply(target, args);
        if (mutating.has(String(prop))) await persist();
        return result;
      };
    },
  }) as OfflineStore;
}

export async function getOfflineStore(): Promise<OfflineStore> {
  if (Platform.OS === "web") {
    if (!singleton) singleton = createMemoryStore();
    return singleton;
  }
  if (singleton) return singleton;
  if (!sqliteFailed) {
    try {
      singleton = await openSqliteStore();
      return singleton;
    } catch {
      sqliteFailed = true;
    }
  }
  singleton = createMemoryStore();
  return singleton;
}

export async function resetOfflineStoreForTests(
  store?: OfflineStore
): Promise<OfflineStore> {
  singleton = store ?? createMemoryStore();
  return singleton;
}

export function clearOfflineStoreForTests(): void {
  singleton = null;
  sqliteFailed = false;
}
